import React, { useCallback, useMemo, useRef } from 'react';
import Joi from '@hapi/joi';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { useMutation, gql } from '@apollo/client';

import Input from 'Components/Input';
import Button from 'Components/Button';
import useReducedErrors from 'Shared/hooks/useReducedErrors';
import ReactGA from 'Shared/analytics';
import { logger } from 'RootShared/utility/ErrorLogger';

const mutationAddExpense = gql`
  mutation RequestFormAddExpenseMutation($input: ExpensesInput!) @api(name: root) {
    addExpensesRequest(expenseRequest: $input) {
      expenseRequest {
        amount
        reason {
          id
        }
        comment
      }
    }
  }
`;

const RequestFormView = ({ formikProps, reasonData }) => {
  const { handleChange, handleSubmit, handleBlur, setFieldValue, errors, values, isSubmitting } = formikProps;

  let reducedErrors = useReducedErrors(errors);

  const isFormDisabled = useMemo(() => {
    return !values.amount || !values.reason || reducedErrors['amount'] || reducedErrors['reason'] || isSubmitting;
  }, [values, reducedErrors, isSubmitting]);

  const onCommentChange = useCallback(
    (e) => {
      setFieldValue('comment', e.target.value);
    },
    [setFieldValue]
  );

  return (
    <div className="form">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <h2 className="h2 form__heading">Expenses request</h2>
        <div className="form__row">
          <div className="form__col">
            <Input
              helper={reducedErrors['amount'] || 'Range from 0 to 30 000'}
              helperVariant={reducedErrors['amount'] ? 'error' : 'message'}
              label="Amount"
              inputMask="99999"
              maskChar=""
              dataTestId={'Amount'}
              fieldProps={{
                name: 'amount',
                placeholder: 'UAH',
                value: values.amount,
                onChange: handleChange('amount'),
                onBlur: handleBlur('amount'),
              }}
            />
          </div>
          <div className="form__col">
            <Input
              label="Reason"
              variant="select"
              helper={reducedErrors['reason'] ? 'Reason must be selected' : ''}
              helperVariant={reducedErrors['reason'] ? 'error' : 'message'}
              options={reasonData}
              callback={(reason) => {
                setFieldValue('reason', reason.id, false);
              }}
              fieldProps={{
                name: 'reason',
                defaultValue: reasonData.length ? reasonData[0] : null,
                onChange: handleChange('reason'),
                onBlur: handleBlur('reason'),
                placeholder: 'Select...',
              }}
            />
          </div>
        </div>
        <Input
          helper={reducedErrors['comment'] || 'Optional'}
          helperVariant="message"
          className="form__comment"
          label="Comment"
          variant="textarea"
          callback={onCommentChange}
          id="comment"
          fieldProps={{
            name: 'comment',
            placeholder: 'Add your comment',
            value: values.comment,
            onChange: handleChange('comment'),
            onBlur: handleBlur('comment'),
          }}
        />
        <div className="form__submit-container">
          <Button dataTestId={'request'} disabled={isFormDisabled} text="Request" type="submit" />
        </div>
      </form>
    </div>
  );
};

const RequestForm = ({ reasonData }) => {
  const [addExpense] = useMutation(mutationAddExpense, {
    onCompleted(data) {
      onRequestExpenseSuccess(data);
    },
    onError(error) {
      logger.error(error);
      onRequestExpenseError(error);
    },
  });

  const initialValues = {
    amount: '',
    comment: '',
    reason: reasonData.length ? reasonData[0].id : '',
  };

  const requestValidationSchema = useMemo(
    () =>
      Joi.object().keys({
        amount: Joi.number()
          .min(0)
          .max(30000)
          .error(() => 'Only numbers in the range from 0 to 30 000 are allowed'),
        comment: Joi.string().allow(''),
        reason: Joi.number().error(() => 'Reason must be selected'),
      }),
    []
  );

  let toastId = useRef(null);

  const showNotification = useCallback(
    () =>
      (toastId.current = toast('In progress, please wait...', {
        autoClose: true,
        closeButton: false,
        hideProgressBar: true,
      })),
    []
  );

  const onRequestExpenseSuccess = useCallback(
    (data) => {
      if (data.addExpensesRequest) {
        toast.update(toastId.current, {
          render: 'Your request has been submitted',
          type: toast.TYPE.SUCCESS,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
      } else {
        ReactGA.exception({
          description: data.error.message,
          fatal: true,
        });
        toast.update(toastId.current, {
          render: 'Error during saving request',
          type: toast.TYPE.ERROR,
          autoClose: 3000,
          closeButton: true,
          hideProgressBar: false,
        });
      }
    },
    [toastId]
  );

  const onRequestExpenseError = useCallback(
    (error) => {
      toast.update(toastId.current, {
        render: error.message || 'Error during saving request',
        type: toast.TYPE.ERROR,
        autoClose: 3000,
        closeButton: true,
        hideProgressBar: false,
      });
    },
    [toastId]
  );

  const handleRequestSubmit = (values, { setValues, setSubmitting }) => {
    const { amount, comment, reason } = values;
    const variables = {
      input: {
        amount: +amount,
        reason: { id: +reason },
        comment,
      },
    };

    ReactGA.event({
      category: 'Expenses request',
      action: 'Add expense request',
      label: reason,
    });

    showNotification();

    if (amount && reason) {
      addExpense({
        variables: variables,
      });
    }

    setSubmitting(false);
    setValues(initialValues);
  };

  const handleRequestValidation = async (values) => {
    let validationResult = await Joi.validate(values, requestValidationSchema, {
      abortEarly: false,
    });

    return validationResult.error;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleRequestSubmit}
      validate={handleRequestValidation}
      validateOnChange={false}
      validateOnBlur={true}
      render={(props) => <RequestFormView formikProps={{ ...props }} reasonData={reasonData} />}
    />
  );
};

export default RequestForm;
